import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  MapPin, Phone, Clock, Shield, Star, CheckCircle, 
  Wrench, ThermometerSun, Award, Users, ArrowRight,
  Building, Zap, Calendar, MessageCircle, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Ankara ilçeleri ve koordinatları
const ankaraIlceler = [
  { slug: 'cankaya', name: 'Çankaya', lat: 39.8731, lng: 32.8597, population: '920.000+' },
  { slug: 'kecioren', name: 'Keçiören', lat: 39.9727, lng: 32.8597, population: '900.000+' },
  { slug: 'yenimahalle', name: 'Yenimahalle', lat: 39.9667, lng: 32.8111, population: '680.000+' },
  { slug: 'mamak', name: 'Mamak', lat: 39.9333, lng: 32.9167, population: '650.000+' },
  { slug: 'etimesgut', name: 'Etimesgut', lat: 39.9500, lng: 32.6833, population: '580.000+' },
  { slug: 'sincan', name: 'Sincan', lat: 39.9697, lng: 32.5833, population: '530.000+' },
  { slug: 'altindag', name: 'Altındağ', lat: 39.9500, lng: 32.8667, population: '370.000+' },
  { slug: 'pursaklar', name: 'Pursaklar', lat: 40.0333, lng: 32.9000, population: '150.000+' },
  { slug: 'golbasi', name: 'Gölbaşı', lat: 39.7833, lng: 32.8000, population: '130.000+' },
  { slug: 'polatli', name: 'Polatlı', lat: 39.5844, lng: 32.1472, population: '120.000+' },
  { slug: 'cubuk', name: 'Çubuk', lat: 40.2333, lng: 33.0333, population: '90.000+' },
  { slug: 'beypazari', name: 'Beypazarı', lat: 40.1667, lng: 31.9167, population: '50.000+' },
  { slug: 'akyurt', name: 'Akyurt', lat: 40.1333, lng: 33.0833, population: '35.000+' },
  { slug: 'kahramankazan', name: 'Kahramankazan', lat: 40.2167, lng: 32.6833, population: '45.000+' },
];

// Kombi markaları - Logo URL'leri güncellenmiş
const kombiMarkalari = [
  { name: 'Vaillant', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fvaillant.png&w=256&q=75', color: '#00A651' },
  { name: 'Buderus', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fbuderus.png&w=256&q=75', color: '#1D4ED8' },
  { name: 'Demirdöküm', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fdemirdokum.png&w=256&q=75', color: '#D97706' },
  { name: 'ECA', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Feca.png&w=256&q=75', color: '#0891B2' },
  { name: 'Viessmann', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fviessmann.png&w=256&q=75', color: '#E30613' },
  { name: 'Baymak', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fbaymak.png&w=256&q=75', color: '#1E3A8A' },
  { name: 'Bosch', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fbosch.png&w=256&q=75', color: '#E30613' },
  { name: 'Ariston', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fariston.png&w=256&q=75', color: '#EF4444' },
  { name: 'Protherm', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fprotherm.png&w=256&q=75', color: '#EA580C' },
  { name: 'Airfel', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fairfel.png&w=256&q=75', color: '#2563EB' },
  { name: 'Ferroli', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fferroli.png&w=256&q=75', color: '#DC2626' },
  { name: 'Alarko', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Falarko.png&w=256&q=75', color: '#7C3AED' },
  { name: 'Daikin', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fdaikin.png&w=256&q=75', color: '#0891B2' },
  { name: 'Warmhaus', logo: 'https://genelmerkezservistalep.com.tr/_next/image?url=%2Flogos%2Fwarmhaus.png&w=256&q=75', color: '#059669' },
  { name: 'Arçelik', logo: null, color: '#1E40AF' },
  { name: 'Beko', logo: null, color: '#DC2626' },
];

// Kapsamlı kombi arıza kodları (SEO için detaylı)
const arizaKodlari = {
  vaillant: [
    { kod: 'F22', anlam: 'Kuru yanma / Su basıncı düşük', cozum: 'Kombi basıncını 1-1.5 bar arasına getirin. Alt taraftaki musluktan su basın.', oncelik: 'high' },
    { kod: 'F28', anlam: 'Ateşleme hatası / Alev oluşmadı', cozum: '3 kez reset atın. Gaz vanasını kontrol edin. Düzelmezse servis çağırın.', oncelik: 'high' },
    { kod: 'F29', anlam: 'Çalışma sırasında alev kaybı', cozum: 'Baca tıkanıklığı veya gaz basıncı sorunu olabilir. Servis çağırın.', oncelik: 'medium' },
    { kod: 'F61', anlam: 'Gaz valfi kontrol hatası', cozum: 'Elektronik kart arızası olabilir. Servis gerekli.', oncelik: 'low' },
    { kod: 'F62', anlam: 'Gaz valfi kapanma gecikmesi', cozum: 'Ana kart ve NTC sensör kontrolü için servis alın.', oncelik: 'low' },
    { kod: 'F63', anlam: 'EEPROM hatası', cozum: 'Ana kart tamiri veya değişimi gerekebilir.', oncelik: 'low' },
    { kod: 'F75', anlam: 'Basınç sensörü arızası', cozum: 'Pompa veya basınç sensörü değişimi. Servis çağırın.', oncelik: 'medium' },
    { kod: 'F05', anlam: 'Egzoz NTC sensör hatası', cozum: 'Baca gazı sensörü kontrolü gerekli.', oncelik: 'medium' },
    { kod: 'F10', anlam: 'Gidiş NTC kısa devre', cozum: 'NTC sensör değişimi gerekli.', oncelik: 'medium' },
    { kod: 'F20', anlam: 'Aşırı ısınma koruma', cozum: 'Su sirkülasyonunu kontrol edin, pompa çalışıyor mu bakın.', oncelik: 'high' },
  ],
  bosch: [
    { kod: 'EA', anlam: 'Ateşleme hatası / Alev algılanmadı', cozum: 'Gaz vanasını kontrol edin. Reset tuşuna 3-5 sn basın.', oncelik: 'high' },
    { kod: 'C6', anlam: 'Fan motoru arızası', cozum: 'Fan bağlantılarını kontrol edin. Servis gerekli.', oncelik: 'medium' },
    { kod: 'C7', anlam: 'Baca tahliye hatası', cozum: 'Fan ve elektronik kartı kontrol ettirin.', oncelik: 'medium' },
    { kod: 'E9', anlam: 'Aşırı sıcaklık sensörü arızası', cozum: 'STB sensör değişimi gerekebilir.', oncelik: 'high' },
    { kod: 'A7', anlam: 'Sıcak su sensörü hatası', cozum: 'DHW-NTC sensör ve kablo kontrolü yapın.', oncelik: 'medium' },
    { kod: 'E2', anlam: 'Gidiş suyu sensörü hatası', cozum: 'NTC sensör değişimi gerekebilir.', oncelik: 'medium' },
    { kod: 'D5', anlam: 'Dönüş suyu sensörü hatası', cozum: 'Sensör bağlantılarını kontrol edin.', oncelik: 'low' },
    { kod: 'CE', anlam: 'Su basıncı düşük', cozum: 'Basıncı 1-1.5 bar arasına getirin.', oncelik: 'high' },
    { kod: 'F0', anlam: 'Dahili hata', cozum: 'Ana kart kontrolü gerekli.', oncelik: 'medium' },
  ],
  baymak: [
    { kod: 'E01', anlam: 'Gaz beslemesi arızası', cozum: 'Gaz vanasını kontrol edin. Doğalgaz kesintisi var mı bakın.', oncelik: 'high' },
    { kod: 'E02', anlam: 'Emniyet termostat arızası', cozum: 'Sensör değişimi gerekebilir. Servis çağırın.', oncelik: 'high' },
    { kod: 'E03', anlam: 'Baca termostat/basınç arızası', cozum: 'Baca sistemini kontrol ettirin.', oncelik: 'medium' },
    { kod: 'E04', anlam: 'Düşük su basıncı', cozum: 'Basıncı 1-1.5 bar arasına getirin.', oncelik: 'high' },
    { kod: 'E05', anlam: 'NTC sensör hatası', cozum: 'Gidiş suyu sensörü kontrolü.', oncelik: 'medium' },
    { kod: 'E06', anlam: 'Dönüş NTC hatası', cozum: 'Dönüş sensörü değişimi gerekebilir.', oncelik: 'low' },
    { kod: 'E10', anlam: 'Su akış sensörü hatası', cozum: 'Türbin temizliği veya değişimi.', oncelik: 'medium' },
    { kod: 'E25', anlam: 'Pompa blokajı', cozum: 'Pompa kontrolü ve servis gerekli.', oncelik: 'high' },
  ],
  demirdokum: [
    { kod: 'E01', anlam: 'Ateşleme başarısızlığı', cozum: 'Gaz vanasını ve elektrik bağlantısını kontrol edin.', oncelik: 'high' },
    { kod: 'E04', anlam: 'Alev oluşmadı', cozum: 'Gaz akışını kontrol edin. Reset atın.', oncelik: 'high' },
    { kod: 'F6', anlam: 'Dönüş NTC hatası', cozum: 'Sensör değişimi gerekebilir.', oncelik: 'medium' },
    { kod: 'F10', anlam: 'Su basıncı hatası', cozum: 'Basıncı kontrol edin, su takviyesi yapın.', oncelik: 'high' },
    { kod: 'F11', anlam: 'Sirkülasyon hatası', cozum: 'Pompa ve vanalar kontrol edilmeli.', oncelik: 'medium' },
    { kod: 'F12', anlam: 'Düşük voltaj hatası', cozum: 'Elektrik tesisatını kontrol ettirin.', oncelik: 'low' },
    { kod: 'F73', anlam: 'Basınç sensör hatası', cozum: 'Sensör veya kablo değişimi gerekebilir.', oncelik: 'medium' },
    { kod: 'F20', anlam: 'Aşırı ısınma koruma', cozum: 'Dolaşım kontrolü yapın.', oncelik: 'high' },
  ],
  ariston: [
    { kod: '101', anlam: 'Aşırı ısınma hatası', cozum: 'Su basıncını kontrol edin. Pompa çalışıyor mu bakın.', oncelik: 'high' },
    { kod: '103', anlam: 'Yetersiz su sirkülasyonu', cozum: 'Filtre ve pompa kontrolü yapın.', oncelik: 'high' },
    { kod: '104', anlam: 'Düşük su basıncı', cozum: '1 bar üzerine basınç getirin.', oncelik: 'high' },
    { kod: '501', anlam: 'Alev algılanmadı', cozum: 'Gaz vanasını ve elektrodu kontrol edin.', oncelik: 'high' },
    { kod: '607', anlam: 'Fan hatası', cozum: 'Fan motoru kontrolü gerekli.', oncelik: 'medium' },
    { kod: '108', anlam: 'Basınç düşüklüğü', cozum: 'Sistemde kaçak kontrolü ve su basma.', oncelik: 'high' },
    { kod: '201', anlam: 'Sıcak su NTC hatası', cozum: 'Sensör değişimi.', oncelik: 'medium' },
  ],
  alarko: [
    { kod: 'E01', anlam: 'Gaz yokluğu / Ateşleme sorunu', cozum: 'Gaz vanalarının açık olduğundan emin olun.', oncelik: 'high' },
    { kod: 'E02', anlam: 'Limit termostat arızası', cozum: 'Kalorifer vanalarını kontrol edin.', oncelik: 'high' },
    { kod: 'E03', anlam: 'Baca termostat hatası', cozum: 'Baca sistemini kontrol ettirin.', oncelik: 'medium' },
    { kod: 'E04', anlam: 'Düşük su basıncı', cozum: 'Basıncı 1-1.5 bar yapın.', oncelik: 'high' },
    { kod: 'E05', anlam: 'NTC sensör arızası', cozum: 'Sıcaklık sensörü kontrolü.', oncelik: 'medium' },
    { kod: 'E10', anlam: 'Yüksek tesisat sıcaklığı', cozum: 'Pompa ve vana kontrolü.', oncelik: 'high' },
  ],
  eca: [
    { kod: 'A01', anlam: 'Ateşleme problemi', cozum: 'Gaz vanalarını kontrol edin. Reset atın.', oncelik: 'high' },
    { kod: 'F04', anlam: 'Gaz yokluğu arızası', cozum: 'Ocağınızı kontrol edin. Gaz saati vanasını açın.', oncelik: 'high' },
    { kod: 'F07', anlam: 'Düşük tesisat basıncı', cozum: 'Basıncı 1 barın üzerine çıkarın.', oncelik: 'high' },
    { kod: 'F37', anlam: 'Çok düşük basınç (0.4 bar)', cozum: 'Acil su basma işlemi yapın.', oncelik: 'high' },
    { kod: 'F10', anlam: 'Gidiş NTC hatası', cozum: 'Sensör kontrolü gerekli.', oncelik: 'medium' },
    { kod: 'F05', anlam: 'Fan hatası', cozum: 'Fan motoru ve bağlantıları kontrol edin.', oncelik: 'medium' },
    { kod: 'F06', anlam: 'Baca sensör hatası', cozum: 'Hava basınç şalteri kontrolü.', oncelik: 'medium' },
  ],
  buderus: [
    { kod: '2P', anlam: 'Su basıncı çok düşük', cozum: 'Basıncı 1-1.5 bar arasına getirin.', oncelik: 'high' },
    { kod: '3A', anlam: 'Fan hatası', cozum: 'Fan motoru ve bağlantıları kontrol edin.', oncelik: 'medium' },
    { kod: '4C', anlam: 'Ateşleme hatası', cozum: 'Gaz vanası ve elektrod kontrolü yapın.', oncelik: 'high' },
    { kod: '6A', anlam: 'Alev sensörü hatası', cozum: 'Elektrod temizliği veya değişimi.', oncelik: 'medium' },
    { kod: '2Y', anlam: 'Döngü arızası', cozum: 'Pompa ve vana kontrolü.', oncelik: 'medium' },
    { kod: 'H07', anlam: 'NTC kısa devre', cozum: 'Sensör değişimi gerekli.', oncelik: 'medium' },
  ],
  viessmann: [
    { kod: 'F2', anlam: 'Brülör kilitlendi', cozum: 'Reset tuşuna basın. Gaz kontrolü yapın.', oncelik: 'high' },
    { kod: 'F3', anlam: 'Çalışma sırasında alev kaybı', cozum: 'Baca ve gaz basıncı kontrolü.', oncelik: 'high' },
    { kod: 'F4', anlam: 'Ateşleme hatası', cozum: 'Elektrod ve trafo kontrolü.', oncelik: 'high' },
    { kod: 'F5', anlam: 'Sıcaklık sınırlayıcı devrede', cozum: 'Aşırı ısınma var, servis çağırın.', oncelik: 'high' },
    { kod: 'F30', anlam: 'NTC kısa devre', cozum: 'Sensör değişimi.', oncelik: 'medium' },
    { kod: 'F38', anlam: 'NTC açık devre', cozum: 'Kablo ve sensör kontrolü.', oncelik: 'medium' },
  ],
  protherm: [
    { kod: 'F1', anlam: 'Aşırı ısınma', cozum: 'Dolaşım kontrolü, pompa ve vana bakın.', oncelik: 'high' },
    { kod: 'F2', anlam: 'Su basıncı düşük', cozum: 'Sistemde su basın.', oncelik: 'high' },
    { kod: 'F3', anlam: 'Alev oluşmadı', cozum: 'Gaz vanası ve elektrod kontrolü.', oncelik: 'high' },
    { kod: 'F4', anlam: 'Alev kaybı', cozum: 'Baca ve gaz kontrolü.', oncelik: 'high' },
    { kod: 'F10', anlam: 'NTC sensör hatası', cozum: 'Sensör değişimi.', oncelik: 'medium' },
  ],
  ferroli: [
    { kod: 'A01', anlam: 'Ateşleme sorunu', cozum: 'Gaz ve elektrod kontrolü.', oncelik: 'high' },
    { kod: 'A03', anlam: 'Aşırı ısınma', cozum: 'Dolaşım sorunu, pompa kontrolü.', oncelik: 'high' },
    { kod: 'F04', anlam: 'Düşük su basıncı', cozum: 'Su basın.', oncelik: 'high' },
    { kod: 'F10', anlam: 'NTC gidiş hatası', cozum: 'Sensör kontrolü.', oncelik: 'medium' },
    { kod: 'F11', anlam: 'NTC dönüş hatası', cozum: 'Sensör değişimi.', oncelik: 'medium' },
    { kod: 'F34', anlam: 'Voltaj düşük', cozum: 'Elektrik tesisatı kontrolü.', oncelik: 'low' },
  ],
};

// 60 Yaygın Kombi Sorunları ve Çözümleri
const yayginSorunlar = [
  { sorun: 'Kombi çalışıyor ama petekler ısınmıyor', cozum: 'Üç yollu vana arızası, sirkülasyon pompası sıkışması veya tesisat filtresi tıkanıklığı. Parça onarımı veya değişimi gerekir.', kategori: 'Isınma' },
  { sorun: 'Sıcak su gelmiyor veya bir ısınıp bir soğuyor', cozum: 'Plaka eşanjör kireçlenmesi, NTC sensör hatası veya akış şalteri arızası. Temizlik ve parça kontrolü gerekir.', kategori: 'Sıcak Su' },
  { sorun: 'Su basıncı sürekli düşüyor', cozum: 'Genleşme tankı havasının bitmesi, ana eşanjörde mikro çatlak veya tesisatta gizli kaçak. Basınç testi ile tespit edilir.', kategori: 'Basınç' },
  { sorun: 'Kombi hiç ateşleme yapmıyor', cozum: 'Gaz vanası, iyonizasyon-ateşleme elektrotları veya anakart arızası. Tüm sistem kontrol edilmeli.', kategori: 'Ateşleme' },
  { sorun: 'Kombiden gürültülü veya vuruntulu ses geliyor', cozum: 'Fan motoru balanssızlığı, pompa kavitasyonu veya tesisat havası. Rulman değişimi veya hava alma gerekir.', kategori: 'Ses' },
  { sorun: 'Kombi patlamalı veya şiddetli yanıyor', cozum: 'Gaz valfi ayarlarının bozulması veya brülörde biriken tozlar. Gaz basınç ayarı ve brülör temizliği yapılmalı.', kategori: 'Yanma' },
  { sorun: 'Ekranda sürekli hata kodu beliriyor', cozum: 'Her kod farklı anlama gelir (F28, E01, C6 vb). Bazen reset yeterli, bazen anakart veya sensör arızası.', kategori: 'Hata' },
  { sorun: 'Doldurma musluğu boşa dönüyor veya sızdırıyor', cozum: 'Conta eskimesi veya yalama. Orijinal doldurma musluğu değişimi gerekir.', kategori: 'Sızıntı' },
  { sorun: 'Kombi sürekli devreye girip çıkıyor', cozum: 'Tesisat filtresi tıkanık veya petek vanaları kapalı. Dolaşım kontrolü yapılmalı.', kategori: 'Döngü' },
  { sorun: 'Yoğuşmalı kombi sürekli su akıtıyor', cozum: 'Yoğuşma giderinin veya sifonunun tıkanması. Tıkanıklık açılmalı ve hazne temizlenmeli.', kategori: 'Yoğuşma' },
  { sorun: 'Fan motoru sürekli çalışıyor', cozum: 'Anakart üzerindeki rölenin yapışık kalması veya prosestat arızası. Elektronik onarım gerekir.', kategori: 'Fan' },
  { sorun: 'Kombiye elektrik gelmiyor', cozum: 'Sigorta atmış, anakart besleme ünitesi yanmış veya kablo kopukluğu. Elektrik altyapısı kontrolü.', kategori: 'Elektrik' },
  { sorun: '3 bar emniyet ventili su damlatıyor', cozum: 'Genleşme tankı sorunu veya doldurma musluğu açık kalmış. Basınç sorunu çözülmeli.', kategori: 'Basınç' },
  { sorun: 'Anakart işlemci veya EEPROM hatası', cozum: 'Kombinin beyni çökmüş. Kart tamiri veya değişimi gerekir, onarım öncelikli.', kategori: 'Anakart' },
  { sorun: 'Peteklerin altı soğuk, üstü sıcak', cozum: 'Peteklerin alt kısmında çamur ve tortu birikimi. Petek temizleme makinesi ile yıkama gerekir.', kategori: 'Petek' },
];

// SSS (SEO için detaylı)
const sikSorulanSorular = [
  {
    soru: 'Kombi bakımı ne sıklıkla yaptırılmalı?',
    cevap: 'Kombilerin yılda en az 1 kez bakımı yaptırılmalıdır. Düzenli bakım kombinin ömrünü uzatır, verimini artırır ve arıza riskini %80 oranında azaltır. Bakım yapılmayan kombilerde gaz kaçağı riski artar ve garanti kapsamı dışına çıkabilir.'
  },
  {
    soru: 'Kombi arıza kodu nasıl resetlenir?',
    cevap: 'Çoğu kombide reset tuşu bulunur. Vaillant\'ta X butonuna 3 saniye basılı tutun. Bosch\'ta reset tuşuna 3-5 saniye basın. Baymak ve Demirdöküm\'de mod/reset butonunu kullanın. Reset sonrası hata devam ederse servis çağırın.'
  },
  {
    soru: 'Kombi basıncı kaç bar olmalı?',
    cevap: 'İdeal kombi basıncı 1-1.5 bar arasında olmalıdır. 0.8 barın altına düşerse hata kodu verir ve çalışmayı durdurur. 2 barın üzerinde ise emniyet ventili açılır. Basınç sürekli düşüyorsa kaçak kontrolü yaptırın.'
  },
  {
    soru: 'Kombi neden sıcak su vermiyor?',
    cevap: 'Sıcak su vermeme nedenleri: 1) 3 yollu vana arızası, 2) Sıcak su sensörü (NTC) hatası, 3) Plaka eşanjör kireçlenmesi, 4) Gaz valfi sorunu. Önce su basıncını kontrol edin, ardından servis çağırın.'
  },
  {
    soru: 'Ankara\'da kombi servisi ne kadar?',
    cevap: 'Ankara\'da 2024 kombi servis ücretleri: Bakım 800-1.500₺, arıza tespit 300-500₺, kartpan değişimi 1.500-3.000₺, eşanjör temizliği 500-800₺, pompa değişimi 1.200-2.500₺. Fiyatlar marka ve modele göre değişir.'
  },
  {
    soru: 'Kombinin ömrü ne kadardır?',
    cevap: 'Düzenli bakım yapılan kombiler 15-20 yıl kullanılabilir. Bakımsız kombiler 8-10 yılda sorun çıkarır. 10 yaşını geçmiş kombilerde verimlilik %20-30 düşer, yeni nesil yoğuşmalı kombiye geçiş düşünülebilir.'
  },
  {
    soru: 'Hangi marka kombi daha iyi?',
    cevap: 'Türkiye\'de en güvenilir kombi markaları: Vaillant (Alman kalitesi, uzun ömür), Bosch (güvenilir, yaygın servis), Baymak (uygun fiyat, yerli), Demirdöküm (güçlü servis ağı). Yoğuşmalı kombiler %30 daha tasarrufludur.'
  },
  {
    soru: 'Kombi kaç yılda bir değiştirilmeli?',
    cevap: '15 yıldan eski kombilerin değiştirilmesi önerilir. Eski kombiler hem verimsiz çalışır hem de parça bulmak zorlaşır. Yeni A sınıfı kombiler %40\'a varan doğalgaz tasarrufu sağlar.'
  },
];

// Google'da en çok aranan kombi terimleri (SEO için)
const populerAramalar = [
  'kombi servisi ankara',
  'ankara kombi tamircisi',
  'acil kombi servisi',
  'kombi arıza kodu',
  'kombi bakım ücreti 2024',
  'kombi basıncı düşük',
  'kombi su basmıyor',
  'kombi yanmıyor',
  'kombi F28 arızası',
  'vaillant arıza kodları',
  'bosch kombi hata kodu',
  'baymak E01 hatası',
  'kombi nasıl resetlenir',
  '7/24 kombi servisi',
  'en yakın kombi ustası',
  'kombi değişim kampanyası',
];

// Hizmetler - 2024 güncel piyasa fiyatları
const hizmetler = [
  { 
    icon: Wrench, 
    title: 'Kombi Arıza', 
    desc: 'Tüm marka ve model kombilerde arıza tespiti ve onarım',
    price: '₺800\'den başlayan'
  },
  { 
    icon: ThermometerSun, 
    title: 'Kombi Bakım', 
    desc: 'Yıllık periyodik bakım ve performans kontrolü',
    price: '₺1.200\'den başlayan'
  },
  { 
    icon: Zap, 
    title: 'Kombi Montaj', 
    desc: 'Profesyonel kombi montaj ve devreye alma',
    price: '₺2.500\'den başlayan'
  },
  { 
    icon: Building, 
    title: 'Petek Temizliği', 
    desc: 'Kalorifer petek temizliği ve bakımı',
    price: '₺400/petek'
  },
];

const AnkaraKombiServisi = () => {
  const navigate = useNavigate();
  const [selectedIlce, setSelectedIlce] = useState(null);
  const [selectedMarka, setSelectedMarka] = useState('vaillant');
  const [openFaq, setOpenFaq] = useState(null);

  // URL'den ilçe bilgisini al
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/\/ankara-(\w+)-kombi-servisi/);
    if (match && match[1] !== 'kombi') {
      const ilceSlug = match[1];
      const found = ankaraIlceler.find(i => i.slug === ilceSlug);
      setSelectedIlce(found || null);
    } else {
      setSelectedIlce(null);
    }
  }, []);

  // SEO için H1, Title ve Schema birebir uyumlu
  const cityName = selectedIlce ? `Ankara ${selectedIlce.name}` : 'Ankara';
  const primaryKeyword = `${cityName} Kombi Servisi`;
  
  const pageTitle = `${primaryKeyword} | 7/24 Aynı Gün Garanti – HizmetGeliyo`;

  const pageDescription = selectedIlce
    ? `${selectedIlce.name} kombi servisi ✓ 7/24 acil servis ✓ Aynı gün hizmet garantisi ✓ Vaillant, Bosch, Baymak, Demirdöküm tüm markalarda arıza, bakım ve montaj. ☎ 0850 242 78 08`
    : `Ankara kombi servisi ✓ 500+ onaylı usta ✓ Aynı gün servis garantisi ✓ Tüm markalarda profesyonel arıza, bakım ve montaj hizmeti. ☎ 0850 242 78 08`;

  const canonicalUrl = selectedIlce 
    ? `https://hizmetgeliyo.com/ankara-${selectedIlce.slug}-kombi-servisi`
    : 'https://hizmetgeliyo.com/ankara-kombi-servisi';

  // Schema.org JSON-LD - Title ve H1 ile birebir uyumlu
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": canonicalUrl,
    "name": primaryKeyword,
    "image": "https://hizmetgeliyo.com/logo.webp",
    "description": pageDescription,
    "url": canonicalUrl,
    "telephone": "+908502427808",
    "priceRange": "₺₺",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": selectedIlce ? selectedIlce.name : "Ankara",
      "addressRegion": "Ankara",
      "addressCountry": "TR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": selectedIlce ? selectedIlce.lat : 39.9334,
      "longitude": selectedIlce ? selectedIlce.lng : 32.8597
    },
    "areaServed": {
      "@type": "City",
      "name": "Ankara",
      "containsPlace": ankaraIlceler.map(i => ({
        "@type": "AdministrativeArea",
        "name": i.name
      }))
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "2847"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Kombi Servisi Hizmetleri",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Kombi Arıza Servisi",
            "description": "Tüm marka kombilerde arıza tespiti ve onarım"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Kombi Bakım Servisi",
            "description": "Yıllık periyodik bakım ve performans kontrolü"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Kombi Montaj",
            "description": "Profesyonel kombi montaj ve devreye alma"
          }
        }
      ]
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Kombi Servisi",
    "provider": {
      "@type": "LocalBusiness",
      "name": "HizmetGeliyo"
    },
    "areaServed": {
      "@type": "City",
      "name": "Ankara"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Kombi Hizmetleri",
      "itemListElement": kombiMarkalari.map(marka => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `${marka.name} Kombi Servisi`
        }
      }))
    }
  };

  // FAQ Schema for Google Rich Results
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `${selectedIlce ? selectedIlce.name + "'de" : "Ankara'da"} kombi servisi ne kadar sürede gelir?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Acil durumlarda 1-2 saat içinde, randevulu işlemlerde aynı gün içinde ustamız adresinize gelir."
        }
      },
      {
        "@type": "Question",
        "name": "Kombi bakım ücreti ne kadar?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kombi bakım ücreti marka ve modele göre 350₺'den başlamaktadır. Kesin fiyat için ücretsiz keşif hizmeti sunuyoruz."
        }
      },
      {
        "@type": "Question",
        "name": "Hangi marka kombilere servis veriyorsunuz?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Vaillant, Bosch, Baymak, Demirdöküm, Ariston, Buderus, Viessmann, Ferroli, Alarko, ECA, Protherm, Airfel, Arçelik, Beko, İmmergas ve Beretta dahil tüm markalara servis veriyoruz."
        }
      },
      {
        "@type": "Question",
        "name": "Garanti kapsamında mıyım?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yaptığımız tüm işlemlerde 1 yıl parça ve işçilik garantisi sunuyoruz. Garanti süresi içinde oluşan arızalarda ücretsiz servis hizmeti alırsınız."
        }
      },
      {
        "@type": "Question",
        "name": "Kombi F28 arızası ne demek?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "F28 hatası genellikle gaz yokluğu veya ateşleme arızasını gösterir. Gaz vanasını kontrol edin ve gazın gelip gelmediğine bakın. Sorun devam ederse servis çağırmanız gerekir."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`ankara kombi servisi, ${selectedIlce ? selectedIlce.name.toLowerCase() + ' kombi servisi, ' : ''}kombi ariza, kombi bakim, kombi tamiri, vaillant servisi, bosch servisi, baymak servisi, demirdokum servisi`} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="tr_TR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <Navbar />
      
      <main className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#0A1628] via-[#1a2d4a] to-[#0A1628] text-white overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-16 lg:py-24">
            <div className="max-w-3xl mx-auto text-center">
              {/* Breadcrumb */}
              <nav className="flex items-center justify-center gap-2 text-sm text-white/60 mb-6">
                <Link to="/" className="hover:text-white transition">Ana Sayfa</Link>
                <span>/</span>
                <Link to="/ankara-kombi-servisi" className="hover:text-white transition">Ankara Kombi Servisi</Link>
                {selectedIlce && (
                  <>
                    <span>/</span>
                    <span className="text-orange-400">{selectedIlce.name}</span>
                  </>
                )}
              </nav>

              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
                <Clock className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">7/24 Acil Servis Hizmeti</span>
              </div>

              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-black mb-6 leading-tight">
                {selectedIlce ? (
                  <>
                    <span className="text-orange-500">{cityName}</span>
                    <br />Kombi Servisi
                  </>
                ) : (
                  <>
                    <span className="text-orange-500">Ankara</span> Kombi Servisi
                  </>
                )}
                </h1>
                
                <p className="text-2xl lg:text-3xl text-white/90 mb-6 font-semibold">
                  7/24 Aynı Gün Servis Garantisi
                </p>

                <p className="text-lg lg:text-xl text-white/80 mb-8 max-w-xl mx-auto">
                  {selectedIlce 
                    ? `${selectedIlce.name} ve çevresinde ${selectedIlce.population} kişiye hizmet veriyoruz. Bulunduğunuz konuma en yakın onaylı ustayı anında yönlendiriyoruz.`
                    : 'Bulunduğun ilçeyi seç, en yakın ustayı anında yönlendirelim. Tüm marka ve modellerde uzman servis.'}
                </p>

                {/* Trust Badges */}
                <div className="flex flex-wrap justify-center gap-4 mb-8">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                    <Shield className="w-5 h-5 text-green-400" />
                    <span className="text-sm">1 Yıl Garanti</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm">4.9 Puan</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    <span className="text-sm">500+ Onaylı Usta</span>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button
                    onClick={() => navigate('/servis-talep/kombi')}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg px-8 py-4 rounded-xl shadow-lg shadow-orange-500/30 h-14"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Hemen Randevu Al
                  </Button>
                  <a
                    href="https://wa.me/908502427808?text=Merhaba,%20Ankara%20kombi%20servisi%20almak%20istiyorum."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold text-lg px-8 py-4 rounded-xl shadow-lg shadow-green-500/30 transition h-14"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                  <a
                    href="tel:+908502427808"
                    className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-bold text-lg px-8 py-4 rounded-xl transition h-14"
                  >
                    <Phone className="w-5 h-5" />
                    0850 242 78 08
                  </a>
                </div>
              </div>
            </div>
        </section>

        {/* Ilce Secimi */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
                İlçeni Seç, <span className="text-orange-500">Hemen Talep Oluştur</span>
              </h2>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                Ankara'nın tüm ilçelerinde profesyonel kombi servisi sunuyoruz. 
                İlçenize tıklayın, hemen servis talebi oluşturun.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {ankaraIlceler.map((i) => (
                <button
                  key={i.slug}
                  onClick={() => navigate(`/servis-talep/kombi?il=Ankara&ilce=${encodeURIComponent(i.name)}`)}
                  className={`group relative p-4 rounded-xl border-2 transition-all hover:shadow-lg hover:-translate-y-1 ${
                    selectedIlce?.slug === i.slug
                      ? 'border-orange-500 bg-orange-50 shadow-lg'
                      : 'border-slate-200 hover:border-orange-300 bg-white'
                  }`}
                >
                  <div className="text-center">
                    <MapPin className={`w-6 h-6 mx-auto mb-2 ${
                      selectedIlce?.slug === i.slug ? 'text-orange-500' : 'text-slate-400 group-hover:text-orange-500'
                    }`} />
                    <h3 className="font-bold text-slate-800 text-sm">{i.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{i.population}</p>
                  </div>
                  {selectedIlce?.slug === i.slug && (
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Hizmetler */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
                Kombi <span className="text-orange-500">Hizmetlerimiz</span>
              </h2>
              <p className="text-slate-600 text-lg">
                {selectedIlce ? `${selectedIlce.name}'de` : 'Ankara genelinde'} sunduğumuz profesyonel kombi hizmetleri
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {hizmetler.map((hizmet, idx) => (
                <div 
                  key={idx}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-slate-100"
                >
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                    <hizmet.icon className="w-7 h-7 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{hizmet.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{hizmet.desc}</p>
                  <div className="text-orange-500 font-bold">{hizmet.price}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Markalar */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
                Tüm <span className="text-orange-500">Markalarda</span> Uzmanız
              </h2>
              <p className="text-slate-600 text-lg">
                Hangi marka kombi kullanırsanız kullanın, uzman ekibimiz hizmetinizde
              </p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
              {kombiMarkalari.map((marka, idx) => (
                <Link
                  key={idx}
                  to={`/servis-talep/kombi?marka=${marka.name.toLowerCase()}`}
                  className="group bg-white hover:bg-slate-50 rounded-xl p-4 text-center transition-all hover:shadow-lg border border-slate-200 hover:border-orange-300"
                >
                  <div className="w-full h-14 mx-auto mb-2 flex items-center justify-center">
                    {marka.logo ? (
                      <img 
                        src={marka.logo} 
                        alt={`${marka.name} Logo`}
                        className="max-h-12 max-w-full object-contain group-hover:scale-110 transition-transform"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-full h-12 rounded-lg items-center justify-center ${marka.logo ? 'hidden' : 'flex'}`}
                      style={{ backgroundColor: marka.color + '15' }}
                    >
                      <span className="text-base font-bold" style={{ color: marka.color }}>
                        {marka.name}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-700 group-hover:text-orange-500 transition-colors text-sm">
                    {marka.name}
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">Servisi</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Ariza Kodlari Bolumu - SEO icin onemli */}
        <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800" id="ariza-kodlari">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block bg-red-500/20 text-red-400 text-sm font-semibold px-4 py-2 rounded-full mb-4">
                🔧 En Kapsamlı Arıza Rehberi
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Kombi <span className="text-orange-500">Arıza Kodları</span> ve Çözümleri
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                Kombinizde hata kodu mu görüyorsunuz? Markanızı seçin ve çözümünü öğrenin. 8 marka, 50+ arıza kodu!
              </p>
            </div>

            {/* Marka Tab'ları */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {Object.keys(arizaKodlari).map((marka) => (
                <button
                  key={marka}
                  onClick={() => setSelectedMarka(marka)}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    selectedMarka === marka 
                      ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' 
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  {marka.charAt(0).toUpperCase() + marka.slice(1)}
                </button>
              ))}
            </div>

            {/* Seçili Marka Arıza Kodları */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {arizaKodlari[selectedMarka]?.map((ariza, idx) => (
                <div 
                  key={idx}
                  className={`bg-white/5 backdrop-blur rounded-xl p-5 border transition-all hover:scale-[1.02] ${
                    ariza.oncelik === 'high' ? 'border-red-500/50 hover:border-red-500' :
                    ariza.oncelik === 'medium' ? 'border-yellow-500/50 hover:border-yellow-500' :
                    'border-slate-600/50 hover:border-slate-500'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                        ariza.oncelik === 'high' ? 'bg-red-500/20' :
                        ariza.oncelik === 'medium' ? 'bg-yellow-500/20' :
                        'bg-slate-500/20'
                      }`}>
                        <span className={`font-bold text-lg ${
                          ariza.oncelik === 'high' ? 'text-red-400' :
                          ariza.oncelik === 'medium' ? 'text-yellow-400' :
                          'text-slate-400'
                        }`}>{ariza.kod}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-white">{ariza.anlam}</h3>
                        {ariza.oncelik === 'high' && (
                          <span className="text-xs bg-red-500/30 text-red-300 px-2 py-0.5 rounded">Acil</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-400">{ariza.cozum}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur rounded-2xl p-8 max-w-2xl mx-auto">
                <h3 className="text-2xl font-bold text-white mb-2">Arıza Kodunuz Çözülmedi mi?</h3>
                <p className="text-slate-400 mb-6">Uzman ekibimiz 7/24 yanınızda. Hemen arayın, aynı gün çözüm!</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="tel:08502427808" className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 font-bold px-6 py-3 rounded-xl hover:bg-slate-100 transition">
                    <Phone className="w-5 h-5" /> 0850 242 78 08
                  </a>
                  <Button
                    onClick={() => navigate('/servis-talep/kombi')}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl"
                  >
                    Online Servis Talebi
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Yaygın Kombi Sorunları ve Çözümleri */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block bg-blue-100 text-blue-600 text-sm font-semibold px-4 py-2 rounded-full mb-4">
                🔧 Profesyonel Çözümler
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
                En Sık Karşılaşılan <span className="text-blue-600">Kombi Sorunları</span>
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Yılların tecrübesiyle derlediğimiz 60+ yaygın arıza ve profesyonel çözümlerimiz
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {yayginSorunlar.map((item, idx) => (
                <div key={idx} className="group bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-white font-bold text-lg">{idx + 1}</span>
                    </div>
                    <div className="flex-1">
                      <span className="inline-block text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full mb-2">{item.kategori}</span>
                      <h3 className="font-bold text-slate-800 mb-2 group-hover:text-blue-600 transition-colors">{item.sorun}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{item.cozum}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <p className="text-slate-500 mb-4">Bu listede sorununuzu bulamadınız mı? Endişelenmeyin, her türlü kombi arızasına çözüm üretiyoruz.</p>
              <a
                href="tel:+908502427808"
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3 rounded-xl transition"
              >
                <Phone className="w-5 h-5" />
                Ücretsiz Arıza Tespiti İçin Hemen Ara
              </a>
            </div>
          </div>
        </section>

        {/* Neden Biz */}
        <section className="py-16 bg-gradient-to-br from-[#0A1628] to-[#1a2d4a] text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Neden <span className="text-orange-500">HizmetGeliyo</span>?
              </h2>
              <p className="text-white/70 text-lg max-w-2xl mx-auto">
                Ankara'da kombi servisi denince akla gelen ilk isim olmamızın nedenleri
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Clock, title: 'Aynı Gün Servis', desc: 'Sabah arayın, akşama ustanız kapınızda' },
                { icon: Shield, title: '1 Yıl Garanti', desc: 'Tüm işlemlerde 1 yıl parça ve işçilik garantisi' },
                { icon: Award, title: 'Uzman Ustalar', desc: '500+ onaylı, sertifikalı kombi ustası' },
                { icon: Star, title: '4.9 Müşteri Puanı', desc: '10.000+ mutlu müşteri, 4.9 ortalama puan' },
              ].map((item, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center hover:bg-white/15 transition">
                  <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-8 h-8 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-white/70">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SSS - SEO için detaylı */}
        <section className="py-16 bg-slate-50" id="sss">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block bg-orange-100 text-orange-600 text-sm font-semibold px-4 py-2 rounded-full mb-4">
                ❓ Merak Edilenler
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
                Sık Sorulan <span className="text-orange-500">Sorular</span>
              </h2>
              <p className="text-slate-600">
                Kombi servisine dair en çok sorulan sorular ve uzman cevapları
              </p>
            </div>

            <div className="space-y-3">
              {sikSorulanSorular.map((faq, idx) => (
                <div 
                  key={idx} 
                  className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition"
                  >
                    <h3 className="font-bold text-slate-800 text-lg pr-4">{faq.soru}</h3>
                    <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-5 border-t border-slate-100">
                      <p className="text-slate-600 pt-4 leading-relaxed">{faq.cevap}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-orange-500">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              {selectedIlce ? `${selectedIlce.name}'de Kombi Sorununuz mu Var?` : 'Kombi Sorununuz mu Var?'}
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Hemen bizi arayın, aynı gün çözüm sunalım. 7/24 hizmetinizdeyiz.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/servis-talep/kombi')}
                className="bg-white text-orange-500 hover:bg-slate-100 font-bold text-lg px-8 py-6 rounded-xl"
              >
                Online Randevu Al
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <a
                href="https://wa.me/908502427808?text=Merhaba,%20kombi%20servisi%20almak%20istiyorum."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold text-lg px-8 py-4 rounded-xl transition"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp ile Ulaşın
              </a>
            </div>
          </div>
        </section>

        {/* Populer Aramalar - SEO icin gizli degil, kullaniciya da yardimci */}
        <section className="py-12 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">
              İlgili Aramalar
            </h3>
            <div className="flex flex-wrap justify-center gap-2">
              {populerAramalar.map((arama, idx) => (
                <Link
                  key={idx}
                  to={`/servis-talep/kombi?q=${encodeURIComponent(arama)}`}
                  className="text-sm bg-white text-slate-600 hover:text-orange-500 hover:border-orange-300 px-4 py-2 rounded-full border border-slate-200 transition-all"
                >
                  {arama}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Diger Ilceler */}
        {selectedIlce && (
          <section className="py-12 bg-white border-t">
            <div className="max-w-7xl mx-auto px-4">
              <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">
                Ankara'nın Diğer İlçelerinde de Hizmet Veriyoruz
              </h3>
              <div className="flex flex-wrap justify-center gap-2">
                {ankaraIlceler.filter(i => i.slug !== selectedIlce?.slug).map((i) => (
                  <Link
                    key={i.slug}
                    to={`/ankara-${i.slug}-kombi-servisi`}
                    className="text-sm text-slate-600 hover:text-orange-500 hover:underline"
                  >
                    {i.name} Kombi Servisi
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Tum Ilceler (Ana sayfa icin) */}
        {!selectedIlce && (
          <section className="py-12 bg-white border-t">
            <div className="max-w-7xl mx-auto px-4">
              <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">
                Ankara'nın Tüm İlçelerinde Hizmet Veriyoruz
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {ankaraIlceler.map((i) => (
                  <Link
                    key={i.slug}
                    to={`/ankara-${i.slug}-kombi-servisi`}
                    className="text-sm text-slate-600 hover:text-orange-500 hover:underline"
                  >
                    {i.name} Kombi Servisi
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
};

export default AnkaraKombiServisi;
